import {Component, TemplateRef, ViewChild} from '@angular/core';
import {SubscribedComponent} from "../subscribed.component";
import {ActivatedRoute, Router} from "@angular/router";
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {BSValidators} from "../shared/bsvalidators";
import moment, {Moment} from "moment";
import {Globals} from "../shared/Globals";
import {emptyRows, mergeRow, mergeWeekRows} from "./week-rows";
import {TimeApprovalRequest, TimeTrackingService, TrackedStatus, TrackedTime} from "../shared/time-tracking.service";
import {NbDialogService, NbMediaBreakpoint, NbThemeService} from "@nebular/theme";
import {EditDialogComponent, EditType} from "../dialogs/edit-dialog/edit-dialog.component";
import {ColDef, ColType} from "../interfaces/colDef";
import {TranslateService} from "@ngx-translate/core";
import {NotifierService} from "../shared/notifier.service";
import {DMenuItem} from "../dialogs/menu-dialog/menu-dialog.component";
import {Subscription} from "rxjs";
import {AuthService} from "../shared/auth.service";
import {SearchComponent} from "../search/search.component";
import {TrackUserResult} from "../interfaces/user";
import {ExportDialogComponent} from "./export-dialog/export-dialog.component";
import {RegistrationWeekService} from "../registration/registration-week.service";
import {UsersService} from "../shared/users.service";

export enum TimeType{
  Tracker = 'tracker',
  Calendar = 'calendar'
}

type TotalType = {
  days: {[key: string]: {hours: number, minutes: number}},
  total: {hours: number, minutes: number}};

@Component({
    selector: 'bst-timesheet',
    templateUrl: './timesheet.component.html',
    styleUrls: ['./timesheet.component.scss'],
    standalone: false
})
export class TimesheetComponent extends SubscribedComponent{

  @ViewChild('projectform') private projectform: TemplateRef<any>;
  @ViewChild('durationform') private durationform: TemplateRef<any>;
  @ViewChild('userResultBodyTemplate', {static: true}) private resultBodyTemplate: TemplateRef<any>;
  @ViewChild('userResultHeaderTemplate', {static: true}) private resultHeaderTempmlate: TemplateRef<any>;
  @ViewChild('userSelectionBodyTemplate', {static: true}) private resultSelectionBodyTemplate: TemplateRef<any>;
  @ViewChild('userSelectionHeaderTemplate', {static: true}) private resultSelectionHeaderTempmlate: TemplateRef<any>;

  today = moment().startOf('day');
  yesterday = this.today.clone().subtract(1, 'day');

  type: TimeType = TimeType.Calendar;
  Math: Math = Math;
  TimeType = TimeType;
  TrackedStatus = TrackedStatus;
  form: FormGroup;
  trackings: {date: Moment, tracks:TrackedTime[]}[] = [];
  weekdays = [1,2,3,4,5,6,7];
  public isMobile = false;
  private readonly mobileBreakpoints = ['xs', 'is', 'sm'];
  public selectedDayIndex = 0;   // 0..6 into weeker.moments; weekday = index + 1
  weeks: {user_to_phase: any, total: any, wtotal:{[key: number]: number},
      open: boolean,
      tracks: {description: string|null, phase: string, Phase_id: number,
        user_to_phase_id?: number, total: number,
        tracks: {[key: number]: number }}[] }[] = [];
  totals: {[key: string]: TotalType
  } = {};
  private assignments: any[] = [];
  // Counts each paramMap navigation (Codex-5/M6): a ?user= lookup awaits a network call, so a
  // route change can fire again before it resolves. Captured at the start of each navigation,
  // checked after the await, so a superseded lookup never sets curuser or reloads the week.
  private navGen = 0;
  internalSuggestions: string[] = [];
  get approvalRequest(): TimeApprovalRequest { return this.week.request; }
  set approvalRequest(r: TimeApprovalRequest) { this.week.request = r; }
  open: TrackedTime = null;
  get date(): Moment { return this.week.date; }
  set date(d: Moment) { this.week.date = d; }
  get curuser(): FormControl { return this.week.user; }

  tabs: any[] = [
    {
      id: TimeType.Calendar,
      title: 'Calendar',
      route: '/registration/hours/' + TimeType.Calendar.toString(),
    },
    {
      id: TimeType.Tracker,
      title: 'Tracker',
      route: '/registration/hours/' + TimeType.Tracker.toString(),
    }
  ];

  constructor(private route: ActivatedRoute,
              private fb: FormBuilder,
              private auth: AuthService,
              public timeTrackingService: TimeTrackingService,
              private dialogService: NbDialogService,
              private notifier: NotifierService,
              private translate: TranslateService,
              private router: Router,
              private themeService: NbThemeService,
              private usersService: UsersService,
              public week: RegistrationWeekService) {
    super();

    this.form = this.fb.group({
      description: [null, []],
      userToPhase: [null, [BSValidators.required]],
      tracked_on: [new Date(), [BSValidators.required]],
      tracked_start: [null, {validators: TimesheetComponent.time, updateOn: 'blur'}],
      tracked_end: [null, {validators: TimesheetComponent.time, updateOn: 'blur'}],
      tracked_hours: [null, []],
      tracked_minutes: [null, []]
    });
  }

  private setupForm(form: FormGroup, checktracking: boolean = false): Subscription[]{

    const d = form.get('tracked_on');
    const s = form.get('tracked_start');
    const e = form.get('tracked_end');
    const m = form.get('tracked_minutes');
    const h = form.get('tracked_hours');

    const updatePanels = (focus: number = 0) => {
      // Only do updates when not tracking
      if(checktracking && this.timeTrackingService.isTracking){
        return;
      }

      const on = moment(d.value).startOf('day');
      const sv = s.value ? moment.duration(s.value) : null;
      const ev = e.value ? moment.duration(e.value) : null;

      const changeDuration = () => {
        const start = this.from(form).clone();
        const end = on.clone().add(ev?.asSeconds(), 'seconds');

        if(start > end){
          end.add(1, 'day');
        }
        const dur = moment.duration(end.diff(start));
        h.setValue(Math.abs(dur.hours()));
        m.setValue(Math.abs(dur.minutes()));
      };

      const updateFromDate = () => {
        const newfrom = this.from(form).clone().add(ev.asSeconds(), 'seconds').subtract(this.duration(form), 'seconds');
        if (!newfrom.isSame(this.from(form), 'day')) {
          form.get('tracked_on').setValue(newfrom.toDate());
        }
        s.setValue(newfrom.format('HH:mm'), {emitEvent: false});
      }

      if(focus === 2){
        if(sv && sv.isValid() && ev && ev.isValid()){
          // Change duration
          changeDuration();
        }
        else if(ev && ev.isValid() && this.to(form)){
          updateFromDate();
        }
      }
      else if(focus === 1){
        if(ev && ev.isValid() && sv && sv.isValid()){
          // Change duration
          changeDuration();
        }
        else if(sv && sv.isValid() && this.duration(form)){
          const end = this.from(form).clone().add(this.duration(form), 'seconds');
          e.setValue(end.format('HH:mm'), {emitEvent: false});
        }
      }
      else{
        if (sv && sv.isValid() && this.duration(form)) {
          const next = this.from(form).clone().add(this.duration(form), 'seconds');
          e.setValue(next.format('HH:mm'), {emitEvent: false});
        } else if (ev && ev.isValid() && this.to(form)) {
          updateFromDate();
        }
      }
    };

    return [
      h.valueChanges.subscribe(() => updatePanels()),
      m.valueChanges.subscribe(() => updatePanels()),
      s.valueChanges.subscribe(() => updatePanels(1)),
      e.valueChanges.subscribe(() => updatePanels(2))
    ];
  }

  ngOnInit(): void {
    super.ngOnInit();

    const subs = this.setupForm(this.form, true);
    this.subs = this.subs.concat(subs);

    const s = this.form.get('tracked_start');
    const e = this.form.get('tracked_end');
    const m = this.form.get('tracked_minutes');
    const h = this.form.get('tracked_hours');
    const p = this.form.get('userToPhase');
    const d = this.form.get('description');

    // Internal activities hours need a description (#1512); offer the ones already used.
    this.subs.push(p.valueChanges.subscribe(v => {
      d.setValidators(this.isInternal(v) ? [BSValidators.required] : []);
      d.updateValueAndValidity({emitEvent: false});
    }));
    this.timeTrackingService.internalSuggestions().then(s => this.internalSuggestions = s ?? []).catch(() => {});

    this.subs.push(this.timeTrackingService.timer.subscribe((sva: TrackedTime) => {
      const o = {emitEvent: false};
      if(sva) {
        s.disable(o);
        e.disable(o);
        h.disable(o);
        m.disable(o);
        p.disable(o);
        d.disable(o);
        this.toForm(sva);
      }
      else{
        s.enable(o);
        e.enable(o);
        h.enable(o);
        m.enable(o);
        p.enable(o);
        d.enable(o);
      }
    }));

    this.subs.push(this.route.paramMap.subscribe(async (paramMap) => {
      const gen = ++this.navGen;
      this.type = <TimeType>(paramMap.get('type') || this.route.snapshot.url[0]?.path);

      // Deep-link from a dashboard nudge to a specific week, e.g. ?week=2026-06-22.
      // page()'s default arg reads this.date at call time, so setting it before reset()
      // is enough - no separate pageWeek() round trip needed.
      const weekParam = this.route.snapshot.queryParamMap.get('week');
      if (weekParam) {
        const w = moment(weekParam);
        if (w.isValid()) {
          this.date = w.startOf('isoWeek');
        }
      }

      const t = this.timeTrackingService.isTracking;
      if(t) {
        this.toForm(t);
        s.disable();
        e.disable();
        h.disable();
        m.disable();
      }

      // Deep-link from an invoice issue (?user=7): only an Admin/Staffer may view another
      // user's week (same gate as the picker in openOthers); a plain User or any lookup
      // error leaves curuser on the signed-in user instead.
      const userParam = +(this.route.snapshot.queryParamMap.get('user') ?? 0);
      if (userParam && this.canOthers) {
        const user = await this.usersService.getUser(userParam).catch(() => null);
        // A newer navigation started while this lookup was in flight: let it drive instead.
        if (gen !== this.navGen) { return; }
        // emitEvent: false so curuser.valueChanges' own reset() below does not also fire; this
        // callback resets exactly once, below.
        if (user) { this.curuser.setValue(user, {emitEvent: false}); }
      }

      if (gen !== this.navGen) { return; }
      await this.reset();

    }));

    this.subs.push(this.curuser.valueChanges.subscribe(async (val) => {
      // C1 (PR8b fix round 3): a manual picker change must supersede an in-flight ?user= lookup
      // above (its own setValue uses emitEvent: false, so it never reaches this subscriber);
      // bumping navGen here makes that lookup's gen check fail and it leaves curuser alone.
      ++this.navGen;
      p.setValue(null);
      await this.reset()
    }));

    this.isMobile = window.innerWidth < 768;
    this.subs.push(this.themeService.onMediaQueryChange()
      .subscribe(([, current]: NbMediaBreakpoint[]) => {
        this.isMobile = this.mobileBreakpoints.includes(current.name);
      }));
    // Default the mobile day selector to today within the shown week.
    this.selectedDayIndex = Math.min(6, Math.max(0, this.today.isoWeekday() - 1));
  }

  private updateTotals(totals){
    for(let i in totals){
      const o = totals[i];
      o.total = this.timeTrackingService.duration(o.total);
      for(let i1 in o.days){
        const o1 = o.days[i1];
        o.days[i1] = this.timeTrackingService.duration(o1);
      }
    }

    for(let i in totals){
      this.totals[i] = totals[i];
    }
  }

  private get rows(): number{
    return this.trackings.reduce((p, c) => {
      return p + (c?.tracks?.length || 0);
    }, 0);
  }

  // Returns whether the page actually landed, so pageWeek() only advances this.date
  // on success (partial failure must leave the old week, including its date, in place).
  private async page(fordate: Moment = this.date): Promise<boolean> {
    // M2: approval strip (shared with Km) disables Submit/Recall/Reopen while a page
    // is in flight, so a click can't land mid-fetch against mixed old/new week state.
    // beginLoad/endLoad (not a boolean) so a stale Hours page finishing after the user
    // has already navigated to Km doesn't re-enable the strip while Km is still loading.
    this.week.beginLoad();
    try {
      return await this.pageImpl(fordate);
    } finally {
      this.week.endLoad();
    }
  }

  private async pageImpl(fordate: Moment = this.date): Promise<boolean> {

    if(this.type === TimeType.Tracker){
      // Tracker view has no per-week approval request; clear any lock carried over from a
      // previously-viewed (Calendar) submitted/approved week, otherwise entryLocked() keeps
      // gating tracker cells on a stale request and commits silently early-return (no save,
      // no toast). The week lock only governs the Calendar grid.
      this.approvalRequest = null;
      // A failed fetch was already surfaced by errorify (toast, or redirect-to-login on
      // 401). Swallow it here and bail without throwing so every caller's
      // `loading.next(false)` still runs (#598: otherwise a rejected /week or /trackings
      // wedges isLoading=true and the page locks).
      const token = this.week.token;
      const tracks = await this.timeTrackingService.getTrackings(TrackedStatus.Complete, this.rows, this.curuser?.value?.id || null).catch(() => null);
      // Stale response guard (same as the Calendar branch below): the picker may have
      // moved to another user while this fetch was in flight, in which case merging it
      // now would mix that other user's tracks into whichever week is on screen.
      if (!tracks || token !== this.week.token) { return false; }
      this.updateTotals(tracks.totals);

      // Add these tracks to our pack
      tracks.trackings.forEach((track) => {
        const date = moment(Globals.fromDateString(track.tracked_on)).startOf('day');
        for(let i=0; i<this.trackings.length; i++){
          if(this.trackings[i].date.isSame(date, 'day')){
            for(let j=0; j<this.trackings[i].tracks.length; j++){
              if(this.trackings[i].tracks[j].id === track.id){
                this.trackings[i].tracks[j] = <TrackedTime>track;
                return;
              }
            }
            this.trackings[i].tracks.push(<TrackedTime>track);
            return;
          }
        }

        this.trackings.push({
          date,
          tracks: [<TrackedTime>track]
        });
      });

      this.trackings.sort((a, b) => {
        return b.date.valueOf() - a.date.valueOf();
      });

      return true;
    }
    else {
      // Fetch the destination week's hours and trips in parallel (M5), and pass the
      // destination week explicitly to fetchTrips (C1): page() runs before this.date
      // is updated by the caller, so a refresh keyed off this.date would otherwise read
      // the week still on screen, leaving tripRows and hasHours describing two different weeks.
      // Capture the facade generation before both requests and use fetchTrips (not
      // refreshTrips): a raw fetch lets us commit tracks and trips together, only once
      // both requests have actually succeeded (Codex 1, Codex 3), instead of refreshTrips
      // self-committing tripRows even when getWeek failed.
      const token = this.week.token;
      const weekStart = Globals.toISODate(fordate.clone().startOf('isoWeek'));
      const [tracks, trips] = await Promise.all([
        this.timeTrackingService.getWeek(Globals.toISODate(fordate), TrackedStatus.Complete, this.curuser?.value?.id || null).catch(() => null),
        this.week.fetchTrips(weekStart),
      ]);
      // see #598: bail on a toasted fetch error, don't wedge isLoading, and don't commit
      // tripRows or advance the date either, old week stays fully in place (Codex 3).
      // trips === null means the trips fetch itself failed (fetchTrips() returns {rows:[]},
      // not null, when trips just aren't allowed for this view), so it's as much a reason
      // to roll back as a failed getWeek (Claude N1): both must succeed to commit either.
      if (!tracks || trips === null || token !== this.week.token) { return false; }
      this.approvalRequest = tracks.approval_request || null;
      this.updateTotals(tracks.totals);
      this.week.tripRows = trips?.rows?.length ?? 0;

      // Refreshing the week on screen (dialog save, submit, recall) keeps the
      // rows that only exist on the client; paging to another week does not.
      if (fordate.isSame(this.date, 'isoWeek')) { this.rebuildWeeks(tracks.trackings, tracks.assignments); }
      else { this.buildWeeks(tracks.trackings, tracks.assignments); }
      return true;
    }
  }

  private buildWeeks(trackings: any[], assignments: any[]) {
    this.assignments = assignments || [];
    this.weeks = [];
    (trackings || []).forEach((o) => {
      let pcur = this.weeks.find((a) => {
        return a.user_to_phase?.phase?.Project_id === o.user_to_phase?.phase?.Project_id;
      });
      if(!pcur){
        pcur = {
          user_to_phase: o.user_to_phase,
          total: 0,
          wtotal: [],
          tracks: [],
          open: true
        };
        this.weeks.push(pcur);
      }

      pcur.total += o.duration;

      let dcur = pcur.tracks.find((a) => {
        return a.description?.trim().toUpperCase() === o.description?.trim().toUpperCase()
          && a.Phase_id === o.user_to_phase?.Phase_id;
      });
      if(!dcur){
        dcur = {
          description: o.description,
          phase: o.user_to_phase?.phase?.name,
          Phase_id: o.user_to_phase?.Phase_id,
          user_to_phase_id: o.user_to_phase?.id,
          total: 0,
          tracks: []
        };
        pcur.tracks.push(dcur);
      }

      const wcur = dcur.tracks[o.weekday];
      const wcur1 = pcur.wtotal[o.weekday];
      dcur.total += o.duration;
      dcur.tracks[o.weekday] = (wcur || 0) + o.duration;
      pcur.wtotal[o.weekday] = (wcur1 || 0) + o.duration;
    });

    // Ensure every active allocation has a row even with zero logged hours (#499)
    (this.assignments).forEach((utp) => {
      let pcur = this.weeks.find(a => a.user_to_phase?.phase?.Project_id === utp.phase?.Project_id);
      if (!pcur) {
        pcur = { user_to_phase: utp, total: 0, wtotal: [], tracks: [], open: true };
        this.weeks.push(pcur);
      }
      const hasBlank = pcur.tracks.find(t => !t.description?.trim() && t.Phase_id === utp.Phase_id);
      if (!hasBlank) {
        pcur.tracks.push({
          description: null,
          phase: utp.phase?.name,
          Phase_id: utp.Phase_id,
          user_to_phase_id: utp.id,
          total: 0,
          tracks: {}
        });
      }
    });
  }

  totalForDate(date: Moment){
    return this.totals[date.year() + '_' + date.isoWeek()]?.days[date.date()];
  }

  duration(form: FormGroup | {tracked_minutes: number, tracked_hours: number} = this.form): number{
    const minutes = parseInt((form instanceof FormGroup ? form.get('tracked_minutes').value : form.tracked_minutes) || 0);
    const hours = parseInt((form instanceof FormGroup ? form.get('tracked_hours').value : form.tracked_hours) || 0);
    return (minutes * 60) + (hours * 60 * 60);
  }

  from(form = this.form): Moment{
    let date = moment(form.get('tracked_on').value);
    date = (date.isValid() ? date : moment()).startOf('day');

    const s = form.get('tracked_start');
    const sv = s.value ? moment.duration(s.value) : null;
    if(sv && sv.isValid()){
      date = date.add(sv.asSeconds(), 'seconds');
    }
    return date;
  }

  // A picker value carries system_key itself, a loaded assignment on its project.
  isInternal(utp: any): boolean {
    return !!(utp?.system_key ?? utp?.phase?.project?.system_key);
  }

  // Internal activities hours need a description (#1512): refuse before any
  // local change and put the input back to what it showed.
  private refuseNoDescription(utp: any, input: HTMLInputElement | undefined, shown: string): boolean {
    if (!this.isInternal(utp)) { return false; }
    this.notifier.notify('tracking.descriptionrequired', null, 'warning', null, 3500);
    if (input) { input.value = shown; }
    return true;
  }

  to(form = this.form): Moment|null{
    if(this.duration(form)){
      return this.from(form).clone().add(this.duration(form), 'seconds');
    }
    return null;
  }

  private fromForm(form = this.form): Partial<TrackedTime>{
    const time = form.getRawValue();
    return {
      description: time.description,
      User_to_Phase_id: time.userToPhase?.id,
      tracked_duration: this.duration(form),
      tracked_start: time.tracked_start ?? null,
      tracked_end: time.tracked_end ?? null,
      tracked_on: Globals.toISODate(time.tracked_on ?? new Date()),
      status: TrackedStatus.Complete,
      created_at: (new Date()).getTime() / 1000
    };
  }

  private toForm(tracked: Partial<TrackedTime>){
    const d = moment.duration(tracked.tracked_duration || 0, 'seconds');

    if(tracked.description) {
      this.form.get('description').setValue(tracked.description);
    }
    if(tracked.user_to_phase) {
      this.form.get('userToPhase').setValue({
        id: tracked.user_to_phase.id,
        client: tracked.user_to_phase.phase?.project?.for_client?.name,
        color: tracked.user_to_phase.phase?.project?.color,
        name: tracked.user_to_phase.phase?.project?.name,
        phase: tracked.user_to_phase.phase?.name,
        system_key: tracked.user_to_phase.phase?.project?.system_key
      });
    }

    // this.form.get('tracked_end').setValue(tracked.status <= 1 ? tracked.tracked_end : moment().format('HH:mm:00'), {emitEvent: false});
    // this.form.get('tracked_on').setValue(moment(tracked.created_at || tracked.tracked_on).toDate(), {emitEvent: false});
    // this.form.get('tracked_start').setValue(tracked.tracked_start, {emitEvent: false});
    // this.form.get('tracked_hours').setValue(Math.trunc(d.asHours()), {emitEvent: false});
    // this.form.get('tracked_minutes').setValue(d.minutes(), {emitEvent: false});

    this.form.patchValue({
      tracked_on: moment(tracked.created_at || tracked.tracked_on).toDate(),
      tracked_start: tracked.tracked_start,
      tracked_end: tracked.status <= 1 ? tracked.tracked_end : moment().format('HH:mm:00'),
      tracked_hours: Math.trunc(d.asHours()),
      tracked_minutes: d.minutes()
    }, {emitEvent: false, onlySelf: true});
  }

  async addTime(others = null) {
    if(this.isLoading){
      return;
    }
    if (this.readonly) { return; }

    this.loading.next(true);
    try {
      const success = others ?
          await this.timeTrackingService.addBulkTime(this.fromForm(), others)
        : await this.timeTrackingService.addTime(this.fromForm(), this.curuser.value?.id);

      if(success){
        this.form.get('tracked_start').reset();
        this.form.get('tracked_end').reset();
        this.form.get('tracked_hours').reset();
        this.form.get('tracked_minutes').reset();

        if(success.tracking && success.totals) {
          if (this.type === TimeType.Calendar) {
            // The calendar grid (and its header, now derived from it) is built
            // from `weeks`, which move()/updateTotals don't touch. Re-fetch the
            // week so a modal-added entry shows in the body and the header stays
            // in sync. (#499)
            await this.page();
          } else {
            this.move(null, success.tracking);
            this.updateTotals(success.totals);
          }
        }
      }
    } catch {
      // A rejected write surfaces its reason via the API toast (a 401 instead
      // redirects to login); re-sync so the grid refreshes and the loading
      // overlay never wedges (#598). Guard the refresh so a malformed payload
      // can't escape the handler as an unhandled rejection.
      await this.page().catch(() => {});
    } finally {
      this.loading.next(false);
    }
  }

  async reset(){
    this.loading.next(true);
    this.trackings = [];
    this.weeks = [];
    this.totals = {};
    await this.page();
    this.loading.next(false);
  }

  async toggleTracking() {
    if(this.isLoading){
      return false;
    }
    if (!this.timeTrackingService.isTracking && this.weekLocked) { return false; }

    this.loading.next(true);
    if(this.timeTrackingService.isTracking){
      await this.timeTrackingService.stop();
    }
    else {
      await this.timeTrackingService.start();
    }

    this.loading.next(false);
  }

  async removeTrack(track: TrackedTime) {
    if (this.entryLocked(track)) { return; }
    if(await this.notifier.confirm('tracking.check', track.description, 'delete', 'danger', 'cancel')) {
      this.loading.next(true);
      try {
        const removed = await this.timeTrackingService.deleteTrack(track);
        if (removed && removed.deleted && this.trackings) {
          track.tracked_on = null;
          this.move(track);

          // we need to retotal tracks for this day
          if(removed.totals){
            this.updateTotals(removed.totals);
          }
        }
      } catch (e) {
        console.error('tracking delete failed', e);
      } finally {
        this.loading.next(false);
      }
    }
  }

  async addTrack(date: Date, duration:number = null, description: string = null, user_to_phase: any = null){
    if (this.weekLockedFor(date)) { return; }
    return await this.editTrack({
      tracked_duration: duration || null,
      description: description || null,
      user_to_phase: user_to_phase || null,
      tracked_on: date
    });
  }

  async commitCell(project: any, trak: any, weekday: number, raw: string, input?: HTMLInputElement) {
    if (this.readonly) { return; }
    const seconds = this.parseDuration(raw);
    const old = trak.tracks[weekday] || 0;
    if (seconds === old) { return; }
    if (seconds && !trak.description?.trim()
      && this.refuseNoDescription(project.user_to_phase, input, this.cellDisplayPadded(trak.tracks[weekday]))) { return; }

    const date = this.weeker.moments[weekday - 1];

    // snapshot the values we mutate so we can roll back if the save fails
    const prevCell = trak.tracks[weekday], prevTrakTotal = trak.total,
          prevWtotal = project.wtotal[weekday], prevProjTotal = project.total;

    // optimistic local update of the cell + row/project sums (all in seconds).
    // Applied before the await so rapid re-edits read the updated `old` and
    // different-cell edits compose (deltas are commutative) without blocking.
    trak.tracks[weekday] = seconds || undefined;
    trak.total = (trak.total || 0) - old + seconds;
    project.wtotal[weekday] = (project.wtotal[weekday] || 0) - old + seconds;
    project.total = (project.total || 0) - old + seconds;

    const rollback = () => {
      trak.tracks[weekday] = prevCell;
      trak.total = prevTrakTotal;
      project.wtotal[weekday] = prevWtotal;
      project.total = prevProjTotal;
    };

    try {
      const res = await this.timeTrackingService.setCell({
        User_to_Phase_id: trak.user_to_phase_id ?? project.user_to_phase?.id ?? null,
        description: trak.description ?? null,
        tracked_on: Globals.toISODate(date.toDate()),
        tracked_duration: seconds
      }, this.curuser.value?.id || null);

      // cell() always returns {totals} on success, so a missing totals body
      // means an unexpected failure: roll the optimistic update back.
      if (res?.totals) { this.updateTotals(res.totals); }
      else { rollback(); }
    } catch {
      // put() rejects on HTTP error (api.service putpost), so a failed save
      // lands here; restore the pre-edit values exactly once.
      rollback();
    }
  }

  /** Parse a cell value: "1:45" -> h:mm, "1.5"/"1,5" -> decimal hours, "1h 45m"/"45m" -> markers. Returns seconds. */
  private parseDuration(raw: string): number {
    const s = (raw ?? '').toString().trim().toLowerCase().replace(',', '.');
    if (!s) { return 0; }
    if (s.includes(':')) {
      const [h, m] = s.split(':');
      return Math.max(0, (parseInt(h, 10) || 0) * 3600 + (parseInt(m, 10) || 0) * 60);
    }
    const hMatch = s.match(/(\d+(?:\.\d+)?)\s*h/);
    const mMatch = s.match(/(\d+)\s*m/);
    if (hMatch || mMatch) {
      const h = hMatch ? parseFloat(hMatch[1]) : 0;
      const m = mMatch ? parseInt(mMatch[1], 10) : 0;
      return Math.max(0, Math.round(h * 3600 + m * 60));
    }
    const n = parseFloat(s);
    return isNaN(n) ? 0 : Math.max(0, Math.round(n * 3600));
  }

  /** Round seconds to whole minutes, then split into hours + minutes with carry
   *  (so 59m59s reads as 1h 00m, never 0h 60m). */
  private hoursMinutes(seconds: number): { h: number, m: number } {
    const total = Math.round(seconds / 60);
    return { h: Math.floor(total / 60), m: total % 60 };
  }

  /** Two-digit minutes for the always-padded "Xh YYm" duration format. */
  pad2(n: number): string {
    return (n || 0).toString().padStart(2, '0');
  }

  /** Cell/entry duration: always "Xh YYm" (minutes zero-padded) so hours and
   *  minutes line up in fixed columns; '' when empty. */
  cellDisplayPadded(seconds: number): string {
    if (!seconds) { return ''; }
    const { h, m } = this.hoursMinutes(seconds);
    return `${h}${this.translate.instant('tracking.hours')} ${this.pad2(m)}${this.translate.instant('tracking.minutes')}`;
  }

  /** Total as a single "Xh YYm" string, matching the entry rows; '' when zero/empty. */
  trackerTotalStr(dur: { hours: number, minutes: number } | null): string {
    if (!dur || (!dur.hours && !dur.minutes)) { return ''; }
    return `${dur.hours || 0}${this.translate.instant('tracking.hours')} ${this.pad2(dur.minutes)}${this.translate.instant('tracking.minutes')}`;
  }

  // Spreadsheet-style keyboard nav between day cells: Enter / Down moves to the
  // same weekday in the next row, Up moves to the previous. Left/Right are left
  // alone so they move the text cursor. Focus change commits via (blur).
  cellKey(e: KeyboardEvent, pi: number, ri: number, weekday: number) {
    let target: number;
    if (e.key === 'Enter' || e.key === 'ArrowDown') { target = ri + 1; }
    else if (e.key === 'ArrowUp') { target = ri - 1; }
    else { return; }
    e.preventDefault();
    const next = document.querySelector<HTMLInputElement>(`[data-cell="${pi}-${target}-${weekday}"]`);
    if (next) { next.focus(); next.select(); }
    else if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); }
  }

  async commitDescription(project: any, trak: any, raw: string, input?: HTMLInputElement) {
    if (this.readonly) { return; }
    const next = (raw ?? '').trim() || null;
    const prev = trak.description ?? null;
    if (next === prev) { return; }

    // A row with no logged hours has no persisted entries to relabel; just set
    // the label locally so a later cell write carries it. Relabel only when the
    // row actually has time (otherwise we'd sweep the blank bucket's entries).
    const hasHours = (trak.total || 0) > 0;
    if (!hasHours) { trak.description = next; return; }
    if (!next && this.refuseNoDescription(project.user_to_phase, input, prev ?? '')) { return; }

    this.loading.next(true);
    const res = await this.timeTrackingService.relabelRow({
      User_to_Phase_id: trak.user_to_phase_id ?? project.user_to_phase?.id ?? null,
      from_description: prev,
      to_description: next,
      week_start: Globals.toISODate(this.date.clone().startOf('isoWeek').toDate())
    }, this.curuser.value?.id || null);

    if (res) {
      if (res.totals) { this.updateTotals(res.totals); }
      this.rebuildWeeks(res.trackings);
    }
    this.loading.next(false);
  }

  /** buildWeeks() from the server, keeping the rows that only exist on the client (no hours yet). */
  private rebuildWeeks(trackings: any[], assignments = this.assignments) {
    const keep = emptyRows(this.weeks);
    this.buildWeeks(trackings, assignments);
    keep.forEach(({utp, row}) => mergeRow(this.weeks, utp, row));
  }

  /** Bring last week's rows (project, phase, description) into this week with the
   *  hours blank, so recurring activities need only their hours retyped (#1228).
   *  Rows stay client-side until a cell is written, same as addRow(). */
  async copyLastWeek() {
    if (this.readonly) { return; }
    this.loading.next(true);
    try {
      const prev = this.date.clone().subtract(1, 'week');
      const res = await this.timeTrackingService.getWeek(Globals.toISODate(prev), TrackedStatus.Complete,
        this.curuser?.value?.id || null, 'Could not copy last week').catch(() => null);
      if (!res || this.readonly) { return; }
      const added = mergeWeekRows(this.weeks, res.trackings);
      this.notifier.notify(added ? 'tracking.copiedrows' : 'tracking.nothingtocopy', null, 'info', null, 3500, {value: added});
    } finally {
      this.loading.next(false);
    }
  }

  addRow(project: any) {
    if (this.readonly) { return; }
    project.tracks.push({
      description: null,
      phase: project.user_to_phase?.phase?.name,
      Phase_id: project.user_to_phase?.Phase_id,
      user_to_phase_id: project.user_to_phase?.id,
      total: 0,
      tracks: {}
    });
  }

  async removeRow(project: any, trak: any) {
    if (this.readonly) { return; }
    // A row with no logged hours has no persisted entries: just drop it locally.
    if ((trak.total || 0) <= 0) {
      const i = project.tracks.indexOf(trak);
      if (i > -1) { project.tracks.splice(i, 1); }
      return;
    }
    if (!await this.notifier.confirm('tracking.check', trak.description, 'delete', 'danger', 'cancel')) { return; }

    this.loading.next(true);
    const res = await this.timeTrackingService.removeRow({
      User_to_Phase_id: trak.user_to_phase_id ?? project.user_to_phase?.id ?? null,
      description: trak.description ?? null,
      week_start: Globals.toISODate(this.date.clone().startOf('isoWeek').toDate())
    }, this.curuser.value?.id || null);

    if (res) {
      if (res.totals) { this.updateTotals(res.totals); }
      this.rebuildWeeks(res.trackings);
    }
    this.loading.next(false);
  }

  async commitTrackerDescription(trak: TrackedTime, raw: string, input?: HTMLInputElement) {
    if (this.entryLocked(trak)) { return; }
    const next = (raw ?? '').trim() || null;
    if (next === (trak.description ?? null)) { return; }
    if (!next && this.refuseNoDescription(trak.user_to_phase, input, trak.description ?? '')) { return; }
    const prev = {description: trak.description};
    trak.description = next;
    await this.saveTrackedEntry(trak, prev);
  }

  async commitTrackerDuration(trak: TrackedTime, raw: string) {
    if (this.entryLocked(trak)) { return; }
    const seconds = this.parseDuration(raw);
    if (seconds === (trak.tracked_duration || 0)) { return; }
    const prev = {tracked_duration: trak.tracked_duration, tracked_end: trak.tracked_end};
    trak.tracked_duration = seconds;
    // Keep a timed entry consistent: slide its end to start + new duration.
    if (trak.tracked_start) {
      trak.tracked_end = moment(trak.tracked_start, 'HH:mm:ss').add(seconds, 'seconds').format('HH:mm:ss');
    }
    await this.saveTrackedEntry(trak, prev);
  }

  // Persist an inline Tracker edit. Sends the full current entry so the
  // partial-update path can't null the description (update() always rewrites it).
  // A rejected save (errorify toasts it) puts the edited fields back.
  private async saveTrackedEntry(trak: TrackedTime, prev: Partial<TrackedTime>) {
    this.loading.next(true);
    try {
      const res = await this.timeTrackingService.updateTime(trak.id, {
        description: trak.description ?? null,
        tracked_duration: trak.tracked_duration ?? 0,
        tracked_start: trak.tracked_start ?? undefined,
        tracked_end: trak.tracked_end ?? undefined,
        tracked_on: Globals.toISODate(moment(trak.tracked_on).toDate()),
        User_to_Phase_id: trak.user_to_phase?.id
      });
      if (res?.totals) { this.updateTotals(res.totals); }
    } catch {
      Object.assign(trak, prev);
    } finally {
      this.loading.next(false);
    }
  }

  async editTrack(track: Partial<TrackedTime>) {
    if(this.isLoading){
      return;
    }
    if (track.tracked_on && this.weekLockedFor(track.tracked_on)) { return; }

    const dur = this.timeTrackingService.duration(track.tracked_duration);
    const defs: ColDef[] = [
      {
        name: 'description',
        title: 'Description',
        type: ColType.AREA,
        length: 250,
        required: this.isInternal(track.user_to_phase),
        subtitle: await this.translate.get('tracking.achieved').toPromise()
      },
      {
        name: 'project',
        title: 'Project',
        type: ColType.CUSTOM,
        display: this.projectform,
        required: true
      },
      {
        name: 'tracked_on',
        title: 'Date',
        type: ColType.DATE,
        required: true,
        range: track.id ? {
          lte: this.today.toDate(),
          gte: null
        } : null,
      },
      {
        name: 'tracked_start',
        title: await this.translate.get('tracking.from').toPromise(),
        type: ColType.TIME,
        required: false,
        form: new FormControl(track.tracked_start, {updateOn: 'blur'})
      },
      {
        name: 'tracked_end',
        title: await this.translate.get('tracking.to').toPromise(),
        type: ColType.TIME,
        required: false,
        form: new FormControl(track.tracked_end, {updateOn: 'blur'})
      },
      {
        name: 'tracked_duration',
        title: await this.translate.get('tracking.duration').toPromise(),
        type: ColType.CUSTOM,
        display: this.durationform,
        required: true
      }
    ];

    let changesubs: Subscription[] = [];
    const sub = this.dialogService.open<EditDialogComponent<any>>(EditDialogComponent, {
      autoFocus: true,
      context: {
        type: track.id ? EditType.EDIT : EditType.ADD,
        title: await this.translate.get('tracking.tracked_amount').toPromise(),
        subtitle: track.user_to_phase?.phase?.project?.name,
        description: defs,
        data: {
          description: track.description,
          project: track.user_to_phase ? {
            id: track.user_to_phase.id,
            name: track.user_to_phase.phase?.project?.name,
            color: track.user_to_phase.phase?.project?.color,
            client: track.user_to_phase.phase?.project?.for_client?.name,
            phase: track.user_to_phase.phase?.name
          } : null,
          tracked_on: Globals.fromDateString(track.tracked_on),
          tracked_start: track.tracked_start,
          tracked_end: track.tracked_end,
          tracked_duration: 5
        },
        formCreated: async (form: FormGroup, self: EditDialogComponent<any>) => {
          // additional form setup
          form.addControl('tracked_hours', new FormControl(dur?.hours || null, []));
          form.addControl('tracked_minutes', new FormControl(dur?.minutes || null, []));
          form.addValidators((f) => {
            const h = f.get('tracked_hours').value;
            const m = f.get('tracked_minutes').value;
            if(!h && !m){
              return {trackingempty: true}
            }
            return null;
          });
          changesubs = this.setupForm(form, false);
        }
      }
    }).onClose.subscribe(async (result) => {
      sub.unsubscribe();
      changesubs.forEach((o) => o.unsubscribe());
      if(result) {
        this.loading.next(true);

        const updates = {
          description: result.description,
          tracked_start: result.tracked_start,
          tracked_end: result.tracked_end,
          User_to_Phase_id: result.project.id,
          tracked_on: Globals.toISODate(result.tracked_on),
          tracked_duration: this.duration(result)
        };

        try {
          if(track.id) {
            const did = await this.timeTrackingService.updateTime(track.id, updates);
            this.updateTotals(did.totals);
            this.move(<TrackedTime>track, did.tracking);
          }
          else{
            // must be new
            await this.timeTrackingService.addTime(updates, this.curuser.value?.id);
            await this.page();
          }
        } catch {
          // The write can be rejected (e.g. the phase assignment no longer
          // resolves for this user/week because it changed server-side); the
          // reason is surfaced via the API toast (a 401 instead redirects to
          // login). Re-sync so the grid's assignment ids reflect current server
          // state. Guarded so the refresh itself can't escape as a rejection.
          await this.page().catch(() => {});
        } finally {
          // Always clear the overlay, even on a rejected write (#598).
          this.loading.next(false);
        }
      }
    });
  }

  private move(from: TrackedTime = null, to: TrackedTime = null){
    let rmed = -1;
    let mved = false;
    for (let i = 0; i < this.trackings.length; i++) {
      const t = this.trackings[i];

      // remove from this day
      if(from) {
        const index = t.tracks?.indexOf(from);
        if (index !== -1) {
          t.tracks.splice(index, 1);
          rmed = i;
        }
      }

      // move to this day
      if(to?.tracked_on && moment(to.tracked_on).isSame(t.date, 'day')){
        t.tracks.push(to);
        t.tracks.sort((a, b) => {
          if(b.tracked_end && !a.tracked_end){
            return 1;
          }
          else if(a.tracked_end && !b.tracked_end){
            return -1;
          }
          return (b.tracked_end || '').localeCompare(a.tracked_end || '');
        });
        mved = true;
      }

      if((mved || !to) && (rmed >= 0 || !from)){
        break;
      }
    }

    // Clean empty tracks
    if(rmed >=0 && !this.trackings[rmed]?.tracks?.length){
      this.trackings.splice(rmed, 1);
    }

    if(to && !mved){

      // We didn't move to another date to move to, try to make a new one
      const mydate = moment(to.tracked_on).startOf('day');
      const item = {
        date: mydate,
        tracks: [to]
      };

      for (let i=0; i<this.trackings.length; i++){
        if(mydate >= this.trackings[i].date){
          // move to this date
          this.trackings.splice(i, 0, item);
          return;
        }
      }

      // put at end
      this.trackings.push(item);
    }
  }

  async scrolled() {
    this.loading.next(true);
    await this.page();
    this.loading.next(false);
  }

  curweek: Moment;
  menu: DMenuItem<TrackedTime>[] = [
    {
      title: 'edit',
      icon: 'edit',
      status: 'primary',
      action: async (t, m) => {
        return await this.editTrack(t);
      }
    },
    {
      title: 'delete',
      icon: 'trash-2',
      status: 'danger',
      action: async (t, m) => {
        return await this.removeTrack(t);
      }
    }
  ];

  allmenu: DMenuItem<TrackedTime>[] = [{
    title: 'tracking.start',
    icon: 'play-circle',
    status: 'primary',
    action: async (t, m) => {
      // A started timer always lands on the logged-in user's own week (no user
      // id to pass), which would be invisible here while viewing someone else's
      // entries.
      if (this.week.viewingOther) { return; }
      if(!this.timeTrackingService.isTracking){
        await this.timeTrackingService.start({
          User_to_Phase_id: t.user_to_phase.id,
          description: t.description ?? null
        });
      }
    }
  }].concat(<any>this.menu);

  // Same reasoning as the action guard above: don't offer "start" at all while
  // viewing another person's week.
  get trackMenu(): DMenuItem<TrackedTime>[] {
    return this.week.viewingOther ? this.menu : this.allmenu;
  }

  // Per-row kebab menu on the Calendar grid (mirrors the Tracker's ⋮ menu).
  rowmenu: DMenuItem<{project: any, trak: any}>[] = [
    {
      title: 'tracking.removeline',
      icon: 'trash-2',
      status: 'danger',
      action: async (d) => { return await this.removeRow(d.project, d.trak); }
    }
  ];

  // Renamed from `week` (deviation: the facade's constructor param needs that name).
  weekHeader(d: Moment, i: number):
    {info: TotalType, week: string, start: Moment, end: Moment}|null {

    const s = d.clone().startOf('isoWeek');
    const e = d.clone().endOf('isoWeek');

    if(!i || s < this.curweek){
      this.curweek = s.clone();
      const info = this.totals[s.year() + '_' + s.isoWeek()];
      return {
        info,
        week: s.format('W'),
        start: s,
        end: e
      };
    }

    return null;
  }

  get weeker(){
    const s = this.date.clone().startOf('isoWeek');
    const days = [];
    const dates = [];
    const moments = [];
    const totals = [];

    // Header per-day + grand totals are derived from the locally-maintained
    // `weeks` grid (the rows actually shown) rather than the separately-fetched
    // server `totals`. This keeps the header strip exactly in sync with the
    // body: it can never show a ghost value after a delete (the server omits a
    // zeroed week from its response, so the old merge could not clear it), and
    // it can never diverge from an out-of-order async cell save. (#499 — Ahmed)
    const dayTotals: {[weekday: number]: number} = {};
    let grandSeconds = 0;
    for (const project of (this.weeks || [])) {
      grandSeconds += project.total || 0;
      for (const weekday of this.weekdays) {
        const v = project.wtotal?.[weekday];
        if (v) { dayTotals[weekday] = (dayTotals[weekday] || 0) + v; }
      }
    }

    for(let i=0; i<7; i++){
      const d = s.clone().add(i, 'day');
      moments.push(d);
      days.push(d.format('ddd'));
      dates.push(d.format('MMM DD'));
      // weekday index is 1..7 (Mon..Sun), so day i maps to weekday i + 1.
      totals.push(this.timeTrackingService.duration(dayTotals[i + 1] || 0));
    }

    const inweek = Math.ceil(moments[0].diff(this.today, 'weeks', true));
    return {
      grand: this.timeTrackingService.duration(grandSeconds),
      days,
      dates,
      totals,
      inweek,
      moments,
      week: s.isoWeek(),
      range: moments[0].format('MMM D') + ' - ' + moments[moments.length-1].format('MMM D, YYYY')
    }
  }

  async pageWeek(to: string|Date|Moment) {
    if(!this.isLoading) {
      let d = null;
      if (to === 'next') {
        d = moment(this.date).startOf('isoWeek').add(1, 'week');
      } else if (to === 'prev') {
        d = moment(this.date).startOf('isoWeek').subtract(1, 'week');
      } else if (to) {
        // The datepicker emits a Moment since NbMomentDateModule became the date service (#1175).
        d = moment(to).startOf('isoWeek');
      }

      this.loading.next(true);
      const ok = await this.page(d);
      if (ok) { this.date = d; }   // Codex 3: don't advance on a failed/stale page
      this.loading.next(false);
    }
  }

  async stepDay(dir: number) {
    // pageWeek() no-ops while isLoading; guard here so we never flip
    // selectedDayIndex to an edge against the OLD week's stale data.
    if (this.isLoading) { return; }
    const next = this.selectedDayIndex + dir;
    if (next < 0) {
      await this.pageWeek('prev');   // reloads the previous week + sets this.date
      this.selectedDayIndex = 6;     // land on Sunday of the previous week
    } else if (next > 6) {
      await this.pageWeek('next');
      this.selectedDayIndex = 0;     // land on Monday of the next week
    } else {
      this.selectedDayIndex = next;
    }
  }

  // Mobile week strip: jump straight to a day in the shown week (no reload).
  selectDay(index: number) {
    if (this.isLoading) { return; }
    this.selectedDayIndex = index;
  }

  // Compact per-day total for a week-strip chip, e.g. "8h" / "4.5h". Rounded to the
  // half-hour (the timesheet's natural granularity) so the glance value stays clean;
  // the exact h:mm total sits in the day headline below. Empty string when the day
  // has no hours (the chip then shows a dot instead).
  dayChipLabel(total: { hours: number, minutes: number } | null): string {
    if (!total || (!total.hours && !total.minutes)) { return ''; }
    const dec = Math.round((total.hours + total.minutes / 60) * 2) / 2;
    return dec + 'h';
  }

  // From/To are plain 24h text fields (a native time input shows AM/PM in Safari on an
  // English OS). Accepts "9", "930", "9:30" or "9.30" and stores "09:30"; anything else
  // is left as typed and fails `time`, so the form refuses it instead of the server.
  static readonly time = Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/);

  normTime(c: AbstractControl, raw: string){
    const m = (raw ?? '').trim().match(/^(\d{1,2})(?:[:.]?(\d{2}))?$/);
    if(m && +m[1] < 24 && +(m[2] ?? 0) < 60){
      c.setValue(m[1].padStart(2, '0') + ':' + (m[2] ?? '00'));
    }
  }

  // Grows the one-line "what did you work on" note with its content instead of scrolling.
  autogrow(el: HTMLTextAreaElement){
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  adate(from: boolean = true){
    const format = 'MMM D';
    const t = this.timeTrackingService.isTracking;
    if(t){
      const c = moment(t.created_at);
      const h = c.clone().add(t.tracked_duration, 'seconds');
      return !c.isSame(h, 'day') ? (from ? c : h).format(format) : null;
    }
    return this.to() && !this.to().isSame(this.from(), 'day') ?
      ' ' + (from ? this.from() : this.to()).format(format) : null;
  }

  get canOthers(): boolean{
    return this.auth.isAdmin || this.auth.isStaffer;
  }

  get weekLocked(): boolean {
    return !!this.approvalRequest && [TrackedStatus.Submitted, TrackedStatus.Approved].includes(this.approvalRequest.status);
  }

  // Same week colours as Km (week-trips cellState): approval is per week, so every filled
  // cell takes the week's colour. A rejected week is editable again but stays red until resubmitted.
  get cellState(): string {
    switch (this.approvalRequest?.status) {
      case TrackedStatus.Submitted: return 'st-sub';
      case TrackedStatus.Approved: return 'st-appr';
      case TrackedStatus.Rejected: return 'st-rej';
      default: return 'st-draft';
    }
  }

  // Own week, submitted/approved: locked, same as before. Someone else's week: always
  // view-only, same as Km's `viewingOther` (#1600 decision 2026-10-07), regardless of
  // that week's status.
  get readonly(): boolean {
    return this.weekLocked || this.week.viewingOther;
  }

  // Hours feeds its live grid totals into the facade through the strip's
  // [hasHours] input; updateTotals() runs before rebuildWeeks() and cell edits
  // change weeks[].total directly, so a write point in this class would read
  // stale totals. A template binding re-evaluates on every change detection.
  get hasHours(): boolean { return (this.weeks || []).some(p => (p.total || 0) > 0); }

  async onWeekChanged() {
    this.loading.next(true);
    try { await this.page(this.date); } finally { this.loading.next(false); }
  }

  private weekLockedFor(date: string|Date): boolean {
    // Viewing someone else's week is read-only regardless of which week that is.
    if (this.week.viewingOther) { return true; }
    if (!this.weekLocked) { return false; }
    return moment(date).startOf('isoWeek').isSame(this.date.clone().startOf('isoWeek'), 'day');
  }

  private entryLocked(track: Partial<TrackedTime>): boolean {
    // Single source of truth: lock by the week's approval state, not a per-row status.
    return track.tracked_on ? this.weekLockedFor(track.tracked_on) : this.readonly;
  }

  async openOthers() {
    const track = this.fromForm();

    const sub = this.dialogService.open<SearchComponent<TrackUserResult>>(SearchComponent, {
      autoFocus: true,
      context: {
        autosearch: true,
        prefill: true,
        target: 'trackusers',
        bodyTemplate: this.resultBodyTemplate,
        headerTemplate: this.resultHeaderTempmlate,
        selectionBodyTemplate: this.resultSelectionBodyTemplate,
        selectionHeaderTemplate: this.resultSelectionHeaderTempmlate,
        hidden: [],
        filters: {
          track
        }
      }
    }).onClose.subscribe(async (response) => {
      sub.unsubscribe();
      if(response?.selections?.length){
        await this.addTime(response.selections.map(o => o.id));
      }
    });
  }

  logo(user: TrackUserResult) {
    return user.logo && user.logo.preview ? Globals.storage(user.logo.preview) : user.provider_avatar;
  }

  /** Open the client detail from a timesheet row's client name (stops the row toggle). */
  openClient(client: any, ev?: Event): void {
    ev?.stopPropagation();
    if (client?.id) { this.router.navigate(['/clients', client.id]); }
  }

  openExport() {
    const sub = this.dialogService.open<ExportDialogComponent>(ExportDialogComponent).onClose.subscribe(async (result) => {
      sub.unsubscribe();
      if (result) {
        await this.timeTrackingService.exportTimesheet(result.start, result.end, this.curuser.value?.id || null, result.format);
      }
    });
  }

  public get headerTitle() { return {title: 'registration.hours', translate: true}; }
}
